import { sfx, tone } from '../../audio/sfx';
import { defineGame } from '../../ui/arcade';
import { rr, text, type ArcadeGame, type Button } from '../game';

// Night-market (夜市) stalls. Every design here is original and drawn procedurally.

const TAU = Math.PI * 2;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const rnd = (a: number, b: number) => a + Math.random() * (b - a);

interface Spark { x: number; y: number; vx: number; vy: number; t: number; life: number; col: string; r: number; g: number }
interface Pop { x: number; y: number; t: number; s: string; col: string }

/** Small shared particle + floating-text helper. */
export function fxKit() {
  let sparks: Spark[] = [];
  let pops: Pop[] = [];
  return {
    clear() {
      sparks = [];
      pops = [];
    },
    burst(x: number, y: number, cols: readonly string[], n = 12, speed = 90, grav = 160) {
      for (let i = 0; i < n; i++) {
        const a = Math.random() * TAU, s = speed * (0.4 + Math.random() * 0.8);
        const life = rnd(0.4, 0.8);
        sparks.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - speed * 0.3, t: life, life, col: cols[i % cols.length], r: rnd(1.5, 3.5), g: grav });
      }
    },
    pop(x: number, y: number, s: string, col = '#ffd84a') {
      pops.push({ x, y, t: 0.9, s, col });
    },
    update(dt: number) {
      for (const p of sparks) {
        p.vy += p.g * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.t -= dt;
      }
      sparks = sparks.filter((p) => p.t > 0);
      for (const p of pops) p.t -= dt;
      pops = pops.filter((p) => p.t > 0);
    },
    draw(c: CanvasRenderingContext2D) {
      for (const p of sparks) {
        c.globalAlpha = clamp(p.t / p.life, 0, 1);
        c.fillStyle = p.col;
        c.beginPath();
        c.arc(p.x, p.y, p.r, 0, TAU);
        c.fill();
      }
      c.globalAlpha = 1;
      for (const p of pops) {
        const k = 1 - p.t / 0.9;
        c.globalAlpha = clamp(p.t / 0.3, 0, 1);
        const sz = 16 + Math.sin(Math.min(1, k * 4) * Math.PI) * 5;
        text(c, p.s, p.x, p.y - k * 34, sz, p.col);
      }
      c.globalAlpha = 1;
    },
  };
}

/** A string of warm bulbs hanging across the top of a stall. */
function bulbs(c: CanvasRenderingContext2D, W: number, y: number, t: number, sag = 10): void {
  c.strokeStyle = 'rgba(40,20,10,0.8)';
  c.lineWidth = 1.5;
  c.beginPath();
  for (let x = 0; x <= W; x += 6) {
    const yy = y + Math.sin((x / W) * Math.PI * 3) ** 2 * sag;
    if (x === 0) c.moveTo(x, yy);
    else c.lineTo(x, yy);
  }
  c.stroke();
  const cols = ['#ffd84a', '#ff7a5a', '#ffe9a8', '#7fe0c0', '#ff9fd0'];
  for (let i = 0; i < 12; i++) {
    const x = 12 + i * ((W - 24) / 11);
    const yy = y + Math.sin((x / W) * Math.PI * 3) ** 2 * sag + 5;
    const on = 0.7 + 0.3 * Math.sin(t * 3 + i * 1.7);
    const g = c.createRadialGradient(x, yy, 0, x, yy, 12);
    g.addColorStop(0, `rgba(255,220,140,${0.45 * on})`);
    g.addColorStop(1, 'rgba(255,220,140,0)');
    c.fillStyle = g;
    c.fillRect(x - 12, yy - 12, 24, 24);
    c.fillStyle = cols[i % cols.length];
    c.globalAlpha = 0.6 + 0.4 * on;
    c.beginPath();
    c.arc(x, yy, 3.5, 0, TAU);
    c.fill();
    c.globalAlpha = 1;
  }
}

/** Night sky + market haze backdrop. */
function nightBg(c: CanvasRenderingContext2D, W: number, H: number, top: string, bottom: string): void {
  const bg = c.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, top);
  bg.addColorStop(1, bottom);
  c.fillStyle = bg;
  c.fillRect(0, 0, W, H);
}

// ---------------------------------------------------------------------------
// 撈金魚 Goldfish scooping
// ---------------------------------------------------------------------------
function goldfish(): ArcadeGame {
  const W = 300, H = 420, CX = 150, CY = 186, R = 134, PR = 25, OFF = 34, TRIES = 3;
  interface Fish { x: number; y: number; a: number; sp: number; turn: number; size: number; kind: number; ph: number; startle: number }
  interface Flying { x: number; y: number; t: number; kind: number; size: number }
  interface Ripple { x: number; y: number; t: number; big: boolean }
  // Body colours: orange, red-white, calico, black, gold (kind 4).
  const BODY = ['#ff8a3a', '#f4f0e8', '#ff9f6a', '#3a3040', '#ffd34e'];
  const SPOT = ['#ffb070', '#e2493b', '#3a3040', '#6a5a78', '#fff3b0'];
  let fish: Fish[] = [];
  let flying: Flying[] = [];
  let ripples: Ripple[] = [];
  let caught: number[] = [];
  let poi = { x: CX, y: CY + 40, under: false, hp: 1, torn: false, tornT: 0, uses: 0 };
  let tries = TRIES, held = { left: false, right: false, up: false, down: false }, endT = 0, shake = 0;
  const fx = fxKit();

  const spawn = () => {
    fish = [];
    for (let i = 0; i < 13; i++) {
      const gold = i < 2;
      const a = Math.random() * TAU, d = Math.sqrt(Math.random()) * (R - 30);
      fish.push({
        x: CX + Math.cos(a) * d, y: CY + Math.sin(a) * d, a: Math.random() * TAU,
        sp: rnd(28, 46) * (gold ? 1.35 : 1), turn: 0, size: gold ? 11 : rnd(8.5, 12.5),
        kind: gold ? 4 : Math.floor(Math.random() * 4), ph: Math.random() * TAU, startle: 0,
      });
    }
  };
  const inBasin = (x: number, y: number) => Math.hypot(x - CX, y - CY) < R - 6;
  const newPoi = () => {
    if (tries <= 0) return;
    poi = { x: poi.x, y: poi.y, under: false, hp: 1, torn: false, tornT: 0, uses: 0 };
    sfx('pop');
  };
  const tear = () => {
    if (poi.torn) return;
    poi.torn = true;
    poi.under = false;
    poi.tornT = 0;
    tries--;
    shake = 0.25;
    sfx('bad');
    fx.burst(poi.x, poi.y, ['#ffffff', '#cfe8ff'], 14, 70);
    for (const f of fish) if (Math.hypot(f.x - poi.x, f.y - poi.y) < 70) f.startle = 1;
    if (tries <= 0) endT = 1.3;
  };
  const dip = () => {
    if (poi.torn) return newPoi();
    if (poi.under || !inBasin(poi.x, poi.y)) return;
    poi.under = true;
    sfx('water');
    ripples.push({ x: poi.x, y: poi.y, t: 0, big: true });
  };
  const lift = () => {
    if (!poi.under || poi.torn) return;
    poi.under = false;
    poi.uses++;
    ripples.push({ x: poi.x, y: poi.y, t: 0, big: false });
    const got = fish.filter((f) => Math.hypot(f.x - poi.x, f.y - poi.y) < PR - f.size * 0.25);
    // Every lift costs a bit of paper; wet fish weigh on it too.
    const load = 0.06 + got.reduce((s, f) => s + (f.kind === 4 ? 0.2 : 0.08 + f.size * 0.006), 0);
    if (got.length && load > poi.hp) {
      for (const f of got) f.startle = 1;
      tear();
      return;
    }
    poi.hp -= load;
    if (!got.length) {
      tone(300, 0.06, 'sine', 0.03);
    } else {
      for (const f of got) {
        fish.splice(fish.indexOf(f), 1);
        flying.push({ x: f.x, y: f.y, t: 0, kind: f.kind, size: f.size });
        const pts = f.kind === 4 ? 3 : 1;
        g.score += pts;
        fx.pop(f.x, f.y - 10, `+${pts}`, f.kind === 4 ? '#ffd84a' : '#ffffff');
        fx.burst(f.x, f.y, f.kind === 4 ? ['#ffd84a', '#fff3b0', '#ffffff'] : ['#8fd8ff', '#ffffff'], f.kind === 4 ? 18 : 10, 80);
      }
      sfx(got.some((f) => f.kind === 4) ? 'pickup' : 'ok');
      if (!fish.length) {
        g.won = true;
        endT = 1.2;
      }
    }
    if (poi.hp <= 0) tear();
  };
  const movePoi = (x: number, y: number) => {
    const d = Math.hypot(x - poi.x, y - poi.y);
    if (poi.under && !poi.torn) {
      // Moving fast through water stresses the paper much more than gliding.
      poi.hp -= d * 0.0011 + Math.max(0, d - 6) * 0.0016;
      if (Math.random() < d * 0.05) ripples.push({ x, y, t: 0, big: false });
      // Splashing about scares the fish; gentle glides don't.
      if (d > 7) for (const f of fish) if (Math.hypot(f.x - x, f.y - y) < PR + 16) f.startle = 0.35;
      if (poi.hp <= 0) tear();
    }
    poi.x = clamp(x, 8, W - 8);
    poi.y = clamp(y, 8, H - 60);
    if (poi.under && !inBasin(poi.x, poi.y)) lift();
  };

  const drawFish = (c: CanvasRenderingContext2D, x: number, y: number, a: number, s: number, kind: number, ph: number, t: number, alpha = 1) => {
    c.save();
    c.translate(x, y);
    c.rotate(a);
    c.globalAlpha = alpha;
    const wag = Math.sin(t * 9 + ph) * 0.45;
    // Tail: a soft double fan.
    c.fillStyle = kind === 4 ? '#ffe680' : kind === 3 ? '#5a4a68' : '#ffb07a';
    c.save();
    c.translate(-s * 0.9, 0);
    c.rotate(wag);
    c.beginPath();
    c.moveTo(0, 0);
    c.quadraticCurveTo(-s * 1.1, -s * 0.9, -s * 1.3, -s * 0.5);
    c.quadraticCurveTo(-s * 0.8, 0, -s * 1.3, s * 0.5);
    c.quadraticCurveTo(-s * 1.1, s * 0.9, 0, 0);
    c.fill();
    c.restore();
    // Side fins.
    c.beginPath();
    c.ellipse(-s * 0.1, -s * 0.55, s * 0.35, s * 0.16, -0.6 + wag * 0.3, 0, TAU);
    c.ellipse(-s * 0.1, s * 0.55, s * 0.35, s * 0.16, 0.6 - wag * 0.3, 0, TAU);
    c.fill();
    // Body.
    c.fillStyle = BODY[kind];
    c.beginPath();
    c.ellipse(0, 0, s, s * 0.58, 0, 0, TAU);
    c.fill();
    c.fillStyle = SPOT[kind];
    c.beginPath();
    c.ellipse(-s * 0.25, -s * 0.12, s * 0.38, s * 0.25, 0.3, 0, TAU);
    c.fill();
    if (kind === 4) {
      c.fillStyle = `rgba(255,255,255,${0.35 + 0.35 * Math.sin(t * 6 + ph)})`;
      c.beginPath();
      c.ellipse(s * 0.2, -s * 0.2, s * 0.3, s * 0.12, 0.2, 0, TAU);
      c.fill();
    }
    // Eyes.
    c.fillStyle = kind === 3 ? '#ffffff' : '#2a1810';
    for (const e of [-1, 1]) {
      c.beginPath();
      c.arc(s * 0.62, e * s * 0.26, s * 0.13, 0, TAU);
      c.fill();
    }
    c.restore();
  };

  const drawPoi = (c: CanvasRenderingContext2D, x: number, y: number, under: boolean, hp: number, torn: boolean, t: number) => {
    // Handle runs down to the finger.
    c.save();
    rr(c, x - 4, y + PR - 2, 8, OFF + 10, 4, '#e2493b');
    c.fillStyle = 'rgba(255,255,255,0.35)';
    c.fillRect(x - 2, y + PR, 2, OFF + 4);
    c.lineWidth = 5;
    c.strokeStyle = '#e2493b';
    c.beginPath();
    c.arc(x, y, PR, 0, TAU);
    c.stroke();
    c.lineWidth = 1.5;
    c.strokeStyle = 'rgba(255,255,255,0.5)';
    c.beginPath();
    c.arc(x, y, PR + 1.5, Math.PI * 1.1, Math.PI * 1.6);
    c.stroke();
    c.beginPath();
    c.arc(x, y, PR - 2, 0, TAU);
    c.clip();
    if (torn) {
      // Ragged leftovers of the paper.
      c.fillStyle = 'rgba(250,250,245,0.8)';
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * TAU + 0.3;
        c.beginPath();
        c.moveTo(x + Math.cos(a) * PR, y + Math.sin(a) * PR);
        c.lineTo(x + Math.cos(a + 0.35) * (PR * 0.45), y + Math.sin(a + 0.35) * (PR * 0.45));
        c.lineTo(x + Math.cos(a + 0.7) * PR, y + Math.sin(a + 0.7) * PR);
        c.fill();
      }
    } else {
      const wet = 1 - clamp(hp, 0, 1);
      c.fillStyle = under ? `rgba(225,240,255,${0.45 - wet * 0.2})` : `rgba(255,255,250,${0.92 - wet * 0.35})`;
      c.fillRect(x - PR, y - PR, PR * 2, PR * 2);
      // Fibres and soggy patches show the wear.
      c.strokeStyle = 'rgba(180,170,150,0.25)';
      c.lineWidth = 0.7;
      for (let i = -3; i <= 3; i++) {
        c.beginPath();
        c.moveTo(x - PR, y + i * 7 + Math.sin(i) * 2);
        c.lineTo(x + PR, y + i * 7 - Math.sin(i) * 2);
        c.stroke();
      }
      if (wet > 0.35) {
        c.fillStyle = `rgba(150,180,210,${(wet - 0.35) * 0.9})`;
        c.beginPath();
        c.arc(x + 4, y - 3, PR * wet * 0.7, 0, TAU);
        c.fill();
      }
      if (wet > 0.7) {
        c.strokeStyle = 'rgba(120,110,100,0.7)';
        c.lineWidth = 1;
        c.beginPath();
        c.moveTo(x - 8, y - 6);
        c.lineTo(x - 1, y + 1 + Math.sin(t * 20) * 0.5);
        c.lineTo(x + 7, y - 2);
        c.stroke();
      }
    }
    c.restore();
  };

  const g: ArcadeGame = {
    w: W, h: H, buttons: ['left', 'a', 'right'], score: 0, over: false,
    reset() {
      spawn();
      flying = [];
      ripples = [];
      caught = [];
      tries = TRIES;
      poi = { x: CX, y: CY + 40, under: false, hp: 1, torn: false, tornT: 0, uses: 0 };
      held = { left: false, right: false, up: false, down: false };
      endT = 0;
      shake = 0;
      fx.clear();
      g.score = 0;
      g.over = false;
      g.won = false;
    },
    press(b: Button, down: boolean) {
      if (b === 'a') return down ? dip() : lift();
      held[b] = down;
    },
    pointer(type, x, y) {
      if (type === 'down') {
        movePoi(x, y - OFF);
        dip();
      } else if (type === 'move') {
        movePoi(x, y - OFF);
      } else {
        lift();
      }
    },
    update(dt) {
      const sp = 150 * dt;
      const dx = (held.right ? 1 : 0) - (held.left ? 1 : 0), dy = (held.down ? 1 : 0) - (held.up ? 1 : 0);
      if (dx || dy) movePoi(poi.x + dx * sp, poi.y + dy * sp);
      if (poi.under && !poi.torn) {
        poi.hp -= dt * 0.07;
        if (poi.hp <= 0) tear();
      }
      if (poi.torn) poi.tornT += dt;
      for (const f of fish) {
        f.ph += dt;
        if (Math.random() < dt * 0.8) f.turn = rnd(-2.2, 2.2);
        // Startled fish swim away from the poi.
        if (f.startle > 0) {
          f.startle -= dt;
          const away = Math.atan2(f.y - poi.y, f.x - poi.x);
          let da = away - f.a;
          da = Math.atan2(Math.sin(da), Math.cos(da));
          f.a += da * dt * 6;
        } else f.a += f.turn * dt;
        // Keep inside the basin.
        const dc = Math.hypot(f.x - CX, f.y - CY);
        if (dc > R - 24) {
          const home = Math.atan2(CY - f.y, CX - f.x);
          let da = home - f.a;
          da = Math.atan2(Math.sin(da), Math.cos(da));
          f.a += da * dt * 4;
        }
        const v = f.sp * (f.startle > 0 ? 2.6 : 1) * (0.75 + 0.25 * Math.sin(f.ph * 2));
        f.x += Math.cos(f.a) * v * dt;
        f.y += Math.sin(f.a) * v * dt;
        const d2 = Math.hypot(f.x - CX, f.y - CY);
        if (d2 > R - 14) {
          f.x = CX + ((f.x - CX) / d2) * (R - 14);
          f.y = CY + ((f.y - CY) / d2) * (R - 14);
        }
      }
      // Caught fish hop into the bag at the bottom.
      for (const fl of flying) fl.t += dt * 1.6;
      for (const fl of flying.filter((q) => q.t >= 1)) caught.push(fl.kind);
      flying = flying.filter((q) => q.t < 1);
      for (const r of ripples) r.t += dt;
      ripples = ripples.filter((r) => r.t < 1);
      shake = Math.max(0, shake - dt);
      fx.update(dt);
      if (endT > 0) {
        endT -= dt;
        if (endT <= 0) g.over = true;
      }
    },
    draw(c, t) {
      nightBg(c, W, H, '#2a1530', '#4a2418');
      c.save();
      if (shake > 0) c.translate(Math.sin(t * 80) * shake * 10, 0);
      // Wooden stall counter and the bulbs.
      c.fillStyle = '#6a3a22';
      c.fillRect(0, 330, W, H - 330);
      c.fillStyle = '#7d4a2c';
      for (let i = 0; i < 6; i++) c.fillRect(0, 336 + i * 15, W, 2);
      bulbs(c, W, 6, t, 8);
      // Basin: blue plastic tub seen from above.
      c.fillStyle = 'rgba(0,0,0,0.35)';
      c.beginPath();
      c.ellipse(CX + 4, CY + 8, R + 12, R + 10, 0, 0, TAU);
      c.fill();
      c.fillStyle = '#2f7fd0';
      c.beginPath();
      c.arc(CX, CY, R + 10, 0, TAU);
      c.fill();
      c.fillStyle = '#5aa6ee';
      c.beginPath();
      c.arc(CX, CY, R + 4, 0, TAU);
      c.fill();
      const wg = c.createRadialGradient(CX - 30, CY - 40, 10, CX, CY, R);
      wg.addColorStop(0, '#8fd8f4');
      wg.addColorStop(1, '#2a8ab8');
      c.fillStyle = wg;
      c.beginPath();
      c.arc(CX, CY, R, 0, TAU);
      c.fill();
      c.save();
      c.beginPath();
      c.arc(CX, CY, R, 0, TAU);
      c.clip();
      // Caustic shimmer.
      c.strokeStyle = 'rgba(255,255,255,0.12)';
      c.lineWidth = 2;
      for (let i = 0; i < 7; i++) {
        c.beginPath();
        const yy = CY - R + i * 42 + Math.sin(t + i) * 6;
        c.moveTo(CX - R, yy);
        c.bezierCurveTo(CX - 40, yy + Math.sin(t * 1.3 + i) * 14, CX + 40, yy - Math.cos(t * 1.1 + i) * 14, CX + R, yy);
        c.stroke();
      }
      // Lamp reflections bob on the surface.
      for (let i = 0; i < 3; i++) {
        c.fillStyle = 'rgba(255,230,160,0.18)';
        c.beginPath();
        c.ellipse(CX - 70 + i * 60, CY - 90 + Math.sin(t * 1.5 + i) * 3, 16, 5, 0, 0, TAU);
        c.fill();
      }
      // Poi under water is drawn beneath the fish swimming above it.
      if (poi.under) drawPoi(c, poi.x, poi.y, true, poi.hp, false, t);
      for (const f of fish) {
        c.fillStyle = 'rgba(10,40,70,0.25)';
        c.beginPath();
        c.ellipse(f.x + 4, f.y + 6, f.size, f.size * 0.5, f.a, 0, TAU);
        c.fill();
      }
      for (const f of fish) {
        const over = poi.under && Math.hypot(f.x - poi.x, f.y - poi.y) < PR - f.size * 0.25;
        drawFish(c, f.x, f.y, f.a, f.size, f.kind, f.ph, t);
        if (over) {
          c.strokeStyle = `rgba(255,255,255,${0.5 + 0.4 * Math.sin(t * 12)})`;
          c.lineWidth = 1.5;
          c.beginPath();
          c.arc(f.x, f.y, f.size + 4, 0, TAU);
          c.stroke();
        }
      }
      for (const r of ripples) {
        c.strokeStyle = `rgba(255,255,255,${(1 - r.t) * 0.6})`;
        c.lineWidth = 1.5;
        c.beginPath();
        c.arc(r.x, r.y, (r.big ? 10 : 6) + r.t * (r.big ? 34 : 18), 0, TAU);
        c.stroke();
      }
      c.restore();
      // Rim highlight.
      c.strokeStyle = 'rgba(255,255,255,0.35)';
      c.lineWidth = 2;
      c.beginPath();
      c.arc(CX, CY, R + 7, Math.PI * 1.05, Math.PI * 1.55);
      c.stroke();

      // Bag of caught fish (bottom-left) and spare pois (bottom-right).
      const BX = 70, BY = 378;
      c.fillStyle = 'rgba(200,235,255,0.35)';
      c.beginPath();
      c.moveTo(BX - 34, BY - 28);
      c.quadraticCurveTo(BX - 44, BY + 30, BX, BY + 32);
      c.quadraticCurveTo(BX + 44, BY + 30, BX + 34, BY - 28);
      c.closePath();
      c.fill();
      c.strokeStyle = 'rgba(255,255,255,0.6)';
      c.lineWidth = 1.5;
      c.stroke();
      rr(c, BX - 12, BY - 36, 24, 8, 3, '#e2493b');
      caught.slice(-8).forEach((k, i) => {
        const fx2 = BX - 18 + (i % 4) * 12, fy = BY - 4 + Math.floor(i / 4) * 14 + Math.sin(t * 2 + i) * 2;
        drawFish(c, fx2, fy, Math.sin(t + i) * 0.6 + (i % 2 ? Math.PI : 0), 6, k, i, t);
      });
      text(c, `🐟 ${g.score}`, BX + 48, BY - 12, 15, '#fff3d0', 'left');
      for (let i = 0; i < TRIES; i++) {
        const left = i < tries;
        const px = 196 + i * 34, py = 372;
        c.globalAlpha = left ? 1 : 0.25;
        c.strokeStyle = '#e2493b';
        c.lineWidth = 3;
        c.beginPath();
        c.arc(px, py, 11, 0, TAU);
        c.stroke();
        c.fillStyle = '#fffdf2';
        c.beginPath();
        c.arc(px, py, 9, 0, TAU);
        c.fill();
        rr(c, px - 2, py + 10, 4, 16, 2, '#e2493b');
        c.globalAlpha = 1;
      }
      // Paper strength bar.
      if (!poi.torn) {
        const hp = clamp(poi.hp, 0, 1);
        rr(c, 186, 404, 96, 8, 4, 'rgba(0,0,0,0.4)');
        rr(c, 186, 404, Math.max(4, 96 * hp), 8, 4, hp > 0.5 ? '#7fe0a0' : hp > 0.25 ? '#ffd34e' : '#ff6a5a');
      }

      // Poi above the water (with its shadow) or torn.
      if (!poi.under) {
        if (inBasin(poi.x, poi.y) && !poi.torn) {
          c.fillStyle = 'rgba(10,40,70,0.25)';
          c.beginPath();
          c.arc(poi.x + 8, poi.y + 10, PR, 0, TAU);
          c.fill();
        }
        drawPoi(c, poi.x, poi.y, false, poi.hp, poi.torn, t);
      }
      for (const fl of flying) {
        const k = fl.t;
        const x = lerp(fl.x, BX, k), y = lerp(fl.y, BY - 4, k) - Math.sin(k * Math.PI) * 60;
        drawFish(c, x, y, k * 8, fl.size * (1 - k * 0.4), fl.kind, 0, t);
      }
      fx.draw(c);
      if (poi.torn && tries > 0 && poi.tornT > 0.4) {
        rr(c, W / 2 - 90, 300, 180, 26, 13, 'rgba(20,10,6,0.7)');
        text(c, '● ＋', W / 2, 313, 14, '#ffd84a');
      }
      c.restore();
    },
    stars: (s) => Math.min(5, Math.floor(s / 4)),
  };
  return g;
}

// ---------------------------------------------------------------------------
// 套圈圈 Ring toss
// ---------------------------------------------------------------------------
function ringToss(): ArcadeGame {
  const W = 300, H = 420, CX = 150, NEAR_Y = 300, FAR_Y = 92, NEAR_HW = 132, FAR_HW = 84, HAND = { x: 150, y: 392 }, RINGS = 10;
  interface Prize { u: number; v: number; kind: number; pts: number; won: boolean; wob: number }
  interface Ring { u: number; v: number; t: number; hit: Prize | null; off: boolean; col: string; landed: boolean; spin: number; sx: number }
  const RING_COLS = ['#ff5a7a', '#ffd34e', '#5ad1ff', '#7fe08a', '#c58aff'];
  let prizes: Prize[] = [];
  let rings: Ring[] = [];
  let left = RINGS, aim = 0, power = 0, phase = 0, charging = false, held = { left: false, right: false }, endT = 0, pointerOn = false;
  const fx = fxKit();

  const yOf = (v: number) => lerp(NEAR_Y, FAR_Y, v);
  const hwOf = (v: number) => lerp(NEAR_HW, FAR_HW, v);
  const sOf = (v: number) => lerp(1, 0.64, v);
  const xOf = (u: number, v: number) => CX + u * hwOf(v);
  const layout = () => {
    prizes = [];
    const rows: [number, number, number][] = [[0.12, 5, 1], [0.4, 4, 2], [0.66, 3, 3]];
    rows.forEach(([v, n, pts]) => {
      for (let i = 0; i < n; i++) {
        const u = n === 1 ? 0 : lerp(-0.78, 0.78, i / (n - 1));
        prizes.push({ u, v, kind: Math.floor(Math.random() * 4), pts, won: false, wob: 0 });
      }
    });
    // Lucky golden pineapple (旺來) at the back.
    prizes.push({ u: 0, v: 0.9, kind: 9, pts: 5, won: false, wob: 0 });
  };
  const powerToV = (p: number) => p * 1.06;
  const throwRing = () => {
    if (!charging) return;
    charging = false;
    if (left <= 0 || endT > 0) return;
    left--;
    const u = clamp(aim + rnd(-0.03, 0.03), -1.05, 1.05), v = powerToV(power) + rnd(-0.02, 0.02);
    let hit: Prize | null = null, near: Prize | null = null;
    for (const p of prizes) {
      if (p.won) continue;
      const du = Math.abs((u - p.u) * hwOf(p.v)), dv = Math.abs(v - p.v);
      if (du < 12 && dv < 0.065) hit = p;
      else if (du < 22 && dv < 0.11) near = p;
    }
    rings.push({ u, v, t: 0, hit, off: v > 1.02 || Math.abs(u) > 1.02, col: RING_COLS[left % RING_COLS.length], landed: false, spin: rnd(6, 10), sx: near && !hit ? (u < near.u ? -1 : 1) : 0 });
    tone(520, 0.12, 'triangle', 0.04, 0, 760);
  };
  const g: ArcadeGame = {
    w: W, h: H, buttons: ['left', 'a', 'right'], score: 0, over: false,
    reset() {
      layout();
      rings = [];
      left = RINGS;
      aim = 0;
      power = 0;
      phase = 0;
      charging = false;
      held = { left: false, right: false };
      endT = 0;
      pointerOn = false;
      fx.clear();
      g.score = 0;
      g.over = false;
      g.won = false;
    },
    press(b, down) {
      if (b === 'a') {
        if (down && left > 0 && !charging) {
          charging = true;
          phase = 0;
        } else if (!down) throwRing();
      } else if (b === 'left' || b === 'right') held[b] = down;
    },
    pointer(type, x) {
      if (type === 'down') {
        pointerOn = true;
        aim = clamp((x - CX) / NEAR_HW, -1, 1);
        if (left > 0) {
          charging = true;
          phase = 0;
        }
      } else if (type === 'move' && pointerOn) aim = clamp((x - CX) / NEAR_HW, -1, 1);
      else if (type === 'up' && pointerOn) {
        pointerOn = false;
        throwRing();
      }
    },
    update(dt) {
      aim = clamp(aim + ((held.right ? 1 : 0) - (held.left ? 1 : 0)) * dt * 0.9, -1, 1);
      if (charging) {
        // Triangle wave so the meter sweeps evenly up and down.
        phase += dt / 1.1;
        const k = phase % 2;
        power = k < 1 ? k : 2 - k;
      }
      for (const r of rings) {
        if (r.landed) continue;
        r.t += dt / 0.75;
        if (r.t >= 1) {
          r.t = 1;
          r.landed = true;
          const x = xOf(r.u, r.v), y = yOf(r.v);
          if (r.hit) {
            const p = r.hit;
            p.won = true;
            p.wob = 1;
            r.u = p.u;
            r.v = p.v;
            g.score += p.pts;
            fx.pop(xOf(p.u, p.v), yOf(p.v) - 40 * sOf(p.v), `+${p.pts}`, p.pts >= 5 ? '#ffd84a' : '#ffffff');
            fx.burst(xOf(p.u, p.v), yOf(p.v) - 10, ['#ffd84a', '#ff7aa0', '#7fe0c0', '#ffffff'], p.pts >= 5 ? 26 : 14, 110);
            sfx(p.pts >= 5 ? 'fanfare' : 'ok');
          } else if (r.off) {
            sfx('drop');
          } else {
            if (r.sx) {
              // Clipped a prize and skidded off it.
              tone(1400, 0.05, 'triangle', 0.04);
              tone(1100, 0.05, 'triangle', 0.03, 0.06);
              r.u += r.sx * 0.08;
            } else sfx('drop');
            fx.burst(x, y, ['#d8b890'], 5, 40, 120);
          }
          if (left <= 0 && rings.every((q) => q.landed)) endT = 1.1;
          if (prizes.every((p) => p.won)) {
            g.won = true;
            endT = 1.1;
          }
        }
      }
      for (const p of prizes) p.wob = Math.max(0, p.wob - dt * 1.5);
      fx.update(dt);
      if (endT > 0) {
        endT -= dt;
        if (endT <= 0) {
          g.over = true;
          g.won = g.won || g.score >= 12;
        }
      }
    },
    draw(c, t) {
      nightBg(c, W, H, '#22142e', '#3a1c20');
      // Awning.
      for (let i = 0; i < 10; i++) {
        c.fillStyle = i % 2 ? '#fff3e0' : '#e2493b';
        c.fillRect(i * 30, 0, 30, 30);
        c.beginPath();
        c.arc(i * 30 + 15, 30, 15, 0, Math.PI);
        c.fill();
      }
      bulbs(c, W, 50, t, 6);
      // Slanted board with perspective.
      c.fillStyle = '#4a2a1a';
      c.beginPath();
      c.moveTo(CX - FAR_HW - 8, FAR_Y - 22);
      c.lineTo(CX + FAR_HW + 8, FAR_Y - 22);
      c.lineTo(CX + NEAR_HW + 12, NEAR_Y + 26);
      c.lineTo(CX - NEAR_HW - 12, NEAR_Y + 26);
      c.fill();
      const bg2 = c.createLinearGradient(0, FAR_Y - 18, 0, NEAR_Y + 20);
      bg2.addColorStop(0, '#c58a52');
      bg2.addColorStop(1, '#e8b478');
      c.fillStyle = bg2;
      c.beginPath();
      c.moveTo(CX - FAR_HW, FAR_Y - 18);
      c.lineTo(CX + FAR_HW, FAR_Y - 18);
      c.lineTo(CX + NEAR_HW, NEAR_Y + 20);
      c.lineTo(CX - NEAR_HW, NEAR_Y + 20);
      c.fill();
      c.strokeStyle = 'rgba(120,70,30,0.3)';
      c.lineWidth = 1;
      for (let i = 0; i < 8; i++) {
        const v = i / 7;
        const y = yOf(v) + 20 - v * 38;
        c.beginPath();
        c.moveTo(xOf(-1, v), y);
        c.lineTo(xOf(1, v), y);
        c.stroke();
      }
      // Aim lane + landing marker.
      if (left > 0) {
        c.setLineDash([4, 6]);
        c.strokeStyle = 'rgba(255,255,255,0.45)';
        c.lineWidth = 2;
        c.beginPath();
        c.moveTo(xOf(aim, -0.05), yOf(-0.05));
        c.lineTo(xOf(aim, 1), yOf(1));
        c.stroke();
        c.setLineDash([]);
        if (charging) {
          const v = powerToV(power);
          const s = sOf(v);
          c.strokeStyle = '#ffffff';
          c.lineWidth = 2;
          c.beginPath();
          c.ellipse(xOf(aim, v), yOf(v), 15 * s, 6 * s, 0, 0, TAU);
          c.stroke();
        }
      }
      // Prizes and landed rings, back to front.
      type Item = { v: number; draw: () => void };
      const items: Item[] = [];
      const ringAt = (x: number, y: number, s: number, col: string, tilt = 0.38) => {
        c.lineWidth = 4.5 * s;
        c.strokeStyle = 'rgba(0,0,0,0.25)';
        c.beginPath();
        c.ellipse(x + 1, y + 2, 15 * s, 15 * s * tilt, 0, 0, TAU);
        c.stroke();
        c.strokeStyle = col;
        c.beginPath();
        c.ellipse(x, y, 15 * s, 15 * s * tilt, 0, 0, TAU);
        c.stroke();
        c.lineWidth = 1.2 * s;
        c.strokeStyle = 'rgba(255,255,255,0.6)';
        c.beginPath();
        c.ellipse(x, y - 1.5 * s, 15 * s, 15 * s * tilt, 0, Math.PI * 1.1, Math.PI * 1.7);
        c.stroke();
      };
      for (const p of prizes) {
        items.push({
          v: p.v,
          draw: () => {
            const x = xOf(p.u, p.v), y = yOf(p.v), s = sOf(p.v);
            c.save();
            c.translate(x, y);
            c.rotate(Math.sin(t * 30) * p.wob * 0.12);
            c.scale(s * (1 + p.wob * 0.15), s * (1 + p.wob * 0.15));
            drawPrize(c, p.kind, t);
            c.restore();
            if (p.won) {
              c.globalAlpha = 0.6 + 0.4 * Math.sin(t * 5 + p.u * 4);
              text(c, '✦', x + 14 * s, y - 34 * s, 10, '#fff3b0');
              c.globalAlpha = 1;
            }
          },
        });
      }
      for (const r of rings) {
        if (!r.landed || r.off) continue;
        items.push({
          v: r.v - 0.001,
          draw: () => {
            const s = sOf(r.v);
            ringAt(xOf(r.u, r.v), yOf(r.v) + (r.hit ? 1 : 0), s, r.col);
          },
        });
      }
      items.sort((a, b) => b.v - a.v).forEach((it) => it.draw());
      // Rings in flight.
      for (const r of rings) {
        if (r.landed) continue;
        const k = r.t, tx = xOf(r.u, r.v), ty = yOf(r.v) - (r.hit ? 30 * sOf(r.v) : 0);
        const x = lerp(HAND.x, tx, k), y = lerp(HAND.y - 20, ty, k) - Math.sin(k * Math.PI) * (90 + r.v * 70);
        const s = lerp(1.25, sOf(r.v), k);
        ringAt(x, y, s, r.col, 0.3 + 0.25 * Math.abs(Math.sin(k * r.spin)));
      }
      // Counter front + remaining rings stack.
      c.fillStyle = '#7a2a22';
      c.fillRect(0, 340, W, H - 340);
      c.fillStyle = '#ffd34e';
      c.fillRect(0, 340, W, 4);
      for (let i = 0; i < left - (charging ? 1 : 0); i++) {
        c.lineWidth = 4;
        c.strokeStyle = RING_COLS[(i + 1) % RING_COLS.length];
        c.beginPath();
        c.ellipse(36 + (i % 5) * 9, 398 - Math.floor(i / 5) * 18 - (i % 5) * 1, 14, 5, 0, 0, TAU);
        c.stroke();
      }
      text(c, `⭕ ${left}`, 36, 362, 13, '#fff3d0', 'center');
      // Hand holding the ring.
      if (left > 0) {
        const bob = charging ? Math.sin(t * 30) * 1.5 : 0;
        c.fillStyle = '#ffd2a8';
        c.beginPath();
        c.ellipse(HAND.x + aim * 30, HAND.y + bob, 16, 12, 0, 0, TAU);
        c.fill();
        if (charging) ringAt(HAND.x + aim * 30, HAND.y - 14 + bob, 1.1, RING_COLS[left % RING_COLS.length], 0.45);
      }
      // Power meter.
      const MX = 268, MT = 352, MB = 408;
      rr(c, MX - 8, MT - 2, 20, MB - MT + 4, 6, 'rgba(0,0,0,0.5)');
      const mg = c.createLinearGradient(0, MB, 0, MT);
      mg.addColorStop(0, '#7fe08a');
      mg.addColorStop(0.6, '#ffd34e');
      mg.addColorStop(1, '#ff5a5a');
      c.fillStyle = mg;
      const ph = (MB - MT) * (charging ? power : 0);
      c.fillRect(MX - 5, MB - ph, 14, ph);
      text(c, '💪', MX + 2, MT - 12, 12, '#fff');
      text(c, `★ ${g.score}`, W - 10, 70, 15, '#ffd84a', 'right');
      fx.draw(c);
    },
    stars: (s) => Math.min(5, Math.floor(s / 5)),
  };
  return g;
}

/** Ring-toss prizes, drawn centred on their base at (0,0). */
function drawPrize(c: CanvasRenderingContext2D, kind: number, t: number): void {
  c.fillStyle = 'rgba(0,0,0,0.25)';
  c.beginPath();
  c.ellipse(2, 2, 12, 4.5, 0, 0, TAU);
  c.fill();
  if (kind === 9) {
    // Golden pineapple on a little stand.
    rr(c, -12, -6, 24, 8, 3, '#b8402e');
    const gl = 0.5 + 0.5 * Math.sin(t * 3);
    const gr = c.createRadialGradient(0, -24, 2, 0, -24, 26);
    gr.addColorStop(0, `rgba(255,230,120,${0.5 * gl})`);
    gr.addColorStop(1, 'rgba(255,230,120,0)');
    c.fillStyle = gr;
    c.fillRect(-26, -50, 52, 52);
    c.fillStyle = '#ffc93a';
    c.beginPath();
    c.ellipse(0, -20, 11, 15, 0, 0, TAU);
    c.fill();
    c.strokeStyle = '#d89a1a';
    c.lineWidth = 1;
    for (let i = -2; i <= 2; i++) {
      c.beginPath();
      c.moveTo(-10, -20 + i * 6 - 6);
      c.lineTo(10, -20 + i * 6 + 6);
      c.moveTo(10, -20 + i * 6 - 6);
      c.lineTo(-10, -20 + i * 6 + 6);
      c.stroke();
    }
    c.fillStyle = '#4fbf5a';
    for (const a of [-0.5, 0, 0.5]) {
      c.save();
      c.translate(0, -33);
      c.rotate(a);
      c.beginPath();
      c.ellipse(0, -7, 3.5, 9, 0, 0, TAU);
      c.fill();
      c.restore();
    }
    return;
  }
  if (kind === 0 || kind === 1) {
    // Soda bottle (green or blue glass).
    const col = kind === 0 ? 'rgba(90,200,140,0.9)' : 'rgba(90,170,240,0.9)';
    rr(c, -8, -30, 16, 30, 5, col);
    rr(c, -3.5, -42, 7, 14, 2, col);
    rr(c, -4.5, -45, 9, 4, 2, '#e2493b');
    rr(c, -8, -20, 16, 8, 0, '#fff3e0');
    c.fillStyle = 'rgba(255,255,255,0.5)';
    c.fillRect(-5, -28, 2, 24);
    return;
  }
  if (kind === 2) {
    // Little round chick plush.
    c.fillStyle = '#ffd34e';
    c.beginPath();
    c.arc(0, -12, 12, 0, TAU);
    c.fill();
    c.fillStyle = '#ff9f43';
    c.beginPath();
    c.moveTo(-3, -12);
    c.lineTo(3, -12);
    c.lineTo(0, -8);
    c.fill();
    c.fillStyle = '#2a1810';
    c.fillRect(-5, -17, 2.5, 3);
    c.fillRect(3, -17, 2.5, 3);
    c.fillStyle = '#ff9fb0';
    c.beginPath();
    c.arc(-7, -10, 2, 0, TAU);
    c.arc(7, -10, 2, 0, TAU);
    c.fill();
    c.fillStyle = '#7fd06a';
    c.beginPath();
    c.ellipse(0, -25, 2.5, 5, 0.4, 0, TAU);
    c.fill();
    return;
  }
  // Gift box with ribbon.
  rr(c, -11, -20, 22, 20, 3, '#9b5fc0');
  c.fillStyle = '#ffd34e';
  c.fillRect(-2, -20, 4, 20);
  c.fillRect(-11, -12, 22, 4);
  c.beginPath();
  c.ellipse(-5, -22, 5, 3, -0.4, 0, TAU);
  c.ellipse(5, -22, 5, 3, 0.4, 0, TAU);
  c.fill();
}

// ---------------------------------------------------------------------------
// 夾娃娃 Claw machine
// ---------------------------------------------------------------------------
interface Plush { x: number; y: number; r: number; kind: number; vy: number; rest: boolean; rot: number; inChute: boolean }

/** Original round plushies: bun-bear, mochi cat, pearl frog, mango chick, sky whale, peach bunny. */
function drawPlush(c: CanvasRenderingContext2D, p: Plush, t: number): void {
  const PAL = [
    { body: '#f4e2c8', ear: '#e8c8a0', cheek: '#ffb0a0' },
    { body: '#fff7f0', ear: '#ffc0d0', cheek: '#ff9fb0' },
    { body: '#9ad86a', ear: '#7cc050', cheek: '#ff9f9f' },
    { body: '#ffc93a', ear: '#ff9f43', cheek: '#ff8f6a' },
    { body: '#8ac8ff', ear: '#6aa8e8', cheek: '#ffa8c0' },
    { body: '#ffb8c8', ear: '#ff98b0', cheek: '#ff7090' },
  ];
  const pal = PAL[p.kind % PAL.length], r = p.r;
  c.save();
  c.translate(p.x, p.y);
  c.rotate(p.rot);
  c.fillStyle = pal.ear;
  switch (p.kind) {
    case 0: // bun-bear: round ears with a little pleat on top
      for (const s of [-1, 1]) {
        c.beginPath();
        c.arc(s * r * 0.62, -r * 0.7, r * 0.3, 0, TAU);
        c.fill();
      }
      break;
    case 1: // mochi cat: pointy ears
      for (const s of [-1, 1]) {
        c.beginPath();
        c.moveTo(s * r * 0.85, -r * 0.35);
        c.lineTo(s * r * 0.6, -r * 1.05);
        c.lineTo(s * r * 0.2, -r * 0.8);
        c.fill();
      }
      break;
    case 2: // pearl frog: eye bumps
      for (const s of [-1, 1]) {
        c.beginPath();
        c.arc(s * r * 0.45, -r * 0.78, r * 0.32, 0, TAU);
        c.fill();
      }
      break;
    case 3: // mango chick: leaf sprout
      c.fillStyle = '#5ab04a';
      c.beginPath();
      c.ellipse(r * 0.12, -r * 1.05, r * 0.14, r * 0.3, 0.5, 0, TAU);
      c.fill();
      break;
    case 4: // sky whale: little spout
      c.fillStyle = '#cfeaff';
      c.beginPath();
      c.arc(0, -r * 1.12, r * 0.12, 0, TAU);
      c.arc(-r * 0.18, -r * 1.25, r * 0.1, 0, TAU);
      c.arc(r * 0.18, -r * 1.25, r * 0.1, 0, TAU);
      c.fill();
      break;
    default: // peach bunny: long floppy ears
      for (const s of [-1, 1]) {
        c.beginPath();
        c.ellipse(s * r * 0.42, -r * 0.95, r * 0.18, r * 0.45, s * 0.25, 0, TAU);
        c.fill();
      }
  }
  // Body with soft shading.
  const g = c.createRadialGradient(-r * 0.3, -r * 0.35, r * 0.1, 0, 0, r);
  g.addColorStop(0, '#ffffff');
  g.addColorStop(0.25, pal.body);
  g.addColorStop(1, pal.ear);
  c.fillStyle = g;
  c.beginPath();
  c.arc(0, 0, r, 0, TAU);
  c.fill();
  if (p.kind === 0) {
    c.strokeStyle = 'rgba(160,120,80,0.5)';
    c.lineWidth = 1.2;
    for (const a of [-0.4, 0, 0.4]) {
      c.beginPath();
      c.moveTo(Math.sin(a) * r * 0.2, -r * 0.95);
      c.lineTo(Math.sin(a) * r * 0.45, -r * 0.65);
      c.stroke();
    }
  }
  if (p.kind === 4) {
    c.fillStyle = '#e8f6ff';
    c.beginPath();
    c.ellipse(0, r * 0.45, r * 0.65, r * 0.38, 0, 0, TAU);
    c.fill();
  }
  // Face.
  const blink = Math.sin(t * 1.3 + p.x) > 0.97;
  c.fillStyle = '#2a1810';
  const ey = p.kind === 2 ? -r * 0.72 : -r * 0.1;
  for (const s of [-1, 1]) {
    if (blink) c.fillRect(s * r * 0.33 - r * 0.1, ey, r * 0.2, r * 0.05);
    else {
      c.beginPath();
      c.arc(s * r * 0.33, ey, r * 0.1, 0, TAU);
      c.fill();
    }
  }
  c.fillStyle = 'rgba(255,255,255,0.9)';
  if (!blink)
    for (const s of [-1, 1]) {
      c.beginPath();
      c.arc(s * r * 0.33 - r * 0.03, ey - r * 0.04, r * 0.035, 0, TAU);
      c.fill();
    }
  c.fillStyle = pal.cheek;
  c.globalAlpha = 0.8;
  for (const s of [-1, 1]) {
    c.beginPath();
    c.ellipse(s * r * 0.55, r * 0.12, r * 0.14, r * 0.09, 0, 0, TAU);
    c.fill();
  }
  c.globalAlpha = 1;
  c.strokeStyle = '#2a1810';
  c.lineWidth = Math.max(1, r * 0.07);
  c.beginPath();
  if (p.kind === 3) {
    c.fillStyle = '#ff9f43';
    c.moveTo(-r * 0.1, r * 0.02);
    c.lineTo(r * 0.1, r * 0.02);
    c.lineTo(0, r * 0.16);
    c.fill();
  } else {
    c.arc(-r * 0.08, r * 0.08, r * 0.08, 0.1, Math.PI - 0.1);
    c.moveTo(r * 0.16, r * 0.08);
    c.arc(r * 0.08, r * 0.08, r * 0.08, 0.1, Math.PI - 0.1);
    c.stroke();
  }
  c.restore();
}

function claw(): ArcadeGame {
  const W = 300, H = 420, L = 18, RGT = 282, TOP = 62, FLOOR = 326, CHUTE_R = 88, RAIL = 74, PLAYS = 5, HOME = (L + CHUTE_R) / 2;
  type State = 'move' | 'drop' | 'close' | 'lift' | 'carry' | 'open' | 'reset';
  let plush: Plush[] = [];
  let state: State = 'move', x = HOME, target = HOME, rope = 0, open = 1, timer = 0, plays = PLAYS, held = { left: false, right: false };
  let grabbed: Plush | null = null, slipAt = 2, progress = 0, endT = 0, downAt = 0, downX = 0, moved = 0, pointerOn = false, playT = 15, flash = 0;
  const fx = fxKit();

  const restY = (p: Plush) => {
    let y = FLOOR - p.r;
    for (const o of plush) {
      if (o === p || !o.rest || o.inChute || o.y < p.y - 1) continue;
      const dx = Math.abs(o.x - p.x), rr2 = o.r + p.r - 3;
      if (dx < rr2) y = Math.min(y, o.y - Math.sqrt(rr2 * rr2 - dx * dx));
    }
    return y;
  };
  const fill = () => {
    plush = [];
    // Lay the pile in loose layers so it looks like a heap, not towers.
    const layers = [5, 4, 3];
    let i = 0;
    layers.forEach((n, li) => {
      for (let k = 0; k < n; k++) {
        const r = rnd(18, 22.5);
        const x0 = CHUTE_R + 28 + li * 18, x1 = RGT - 24 - li * 18;
        const p: Plush = { x: lerp(x0, x1, n === 1 ? 0.5 : k / (n - 1)) + rnd(-5, 5), y: 0, r, kind: (i++ * 5) % 6, vy: 0, rest: true, rot: rnd(-0.25, 0.25), inChute: false };
        p.y = restY(p);
        plush.push(p);
      }
    });
  };
  const tipY = () => RAIL + 26 + rope;
  const drop = () => {
    if (state !== 'move' || plays <= 0 || endT > 0) return;
    state = 'drop';
    sfx('click');
  };
  const nextPlay = () => {
    plays--;
    if (plays <= 0) {
      endT = 1.2;
      g.won = g.score >= 2;
    } else {
      state = 'move';
      target = x;
      playT = 15;
    }
  };
  const tryGrab = () => {
    const ty = tipY();
    let best: Plush | null = null, bd = 1e9;
    for (const p of plush) {
      if (!p.rest || p.inChute) continue;
      const dx = Math.abs(p.x - x);
      if (dx > p.r * 0.95 || ty < p.y - p.r - 10) continue;
      const d = dx + Math.abs(p.y - p.r * 0.3 - ty) * 0.3;
      if (d < bd) {
        bd = d;
        best = p;
      }
    }
    if (!best) return;
    const off = Math.abs(best.x - x) / best.r;
    const grip = rnd(0.5, 1.05) * (1 - off * off * 0.7);
    const weight = 0.42 + (best.r - 18) * 0.03;
    if (grip < weight * 0.6) {
      // Claws brush it; it just wobbles.
      best.rot += rnd(-0.3, 0.3);
      return;
    }
    grabbed = best;
    best.rest = false;
    slipAt = grip >= weight ? 2 : rnd(0.05, 0.9);
  };
  const slip = () => {
    if (!grabbed) return;
    grabbed.rest = false;
    grabbed.vy = 0;
    grabbed = null;
    tone(500, 0.2, 'sine', 0.05, 0, 250);
  };
  const g: ArcadeGame = {
    w: W, h: H, buttons: ['left', 'a', 'right'], score: 0, over: false,
    reset() {
      fill();
      state = 'move';
      x = target = HOME;
      rope = 0;
      open = 1;
      timer = 0;
      plays = PLAYS;
      held = { left: false, right: false };
      grabbed = null;
      slipAt = 2;
      progress = 0;
      endT = 0;
      pointerOn = false;
      playT = 15;
      flash = 0;
      fx.clear();
      g.score = 0;
      g.over = false;
      g.won = false;
    },
    press(b, down) {
      if (b === 'a') {
        if (down) drop();
      } else if (b === 'left' || b === 'right') {
        held[b] = down;
        if (down) target = x;
      }
    },
    pointer(type, px) {
      if (type === 'down') {
        pointerOn = true;
        downAt = performance.now();
        downX = px;
        moved = 0;
      } else if (type === 'move' && pointerOn) {
        moved = Math.max(moved, Math.abs(px - downX));
        if (moved > 8 && state === 'move') target = clamp(px, L + 16, RGT - 16);
      } else if (type === 'up' && pointerOn) {
        pointerOn = false;
        if (moved < 8 && performance.now() - downAt < 350) drop();
      }
    },
    update(dt) {
      timer += dt;
      flash = Math.max(0, flash - dt);
      const SP = 110;
      if (state === 'move') {
        const dir = (held.right ? 1 : 0) - (held.left ? 1 : 0);
        if (dir) target = clamp(x + dir * 40, L + 16, RGT - 16);
        x += clamp(target - x, -SP * dt, SP * dt);
        if (plays > 0 && endT <= 0) {
          playT -= dt;
          if (playT <= 0) drop();
        }
      } else if (state === 'drop') {
        rope += 150 * dt;
        open = Math.min(1.3, open + dt * 2);
        const ty = tipY();
        const under = plush.filter((p) => p.rest && !p.inChute && Math.abs(p.x - x) < p.r * 0.7);
        const stop = under.length ? Math.min(...under.map((p) => p.y - p.r * 0.35)) : FLOOR - 8;
        if (ty >= stop || ty >= FLOOR - 8) {
          state = 'close';
          timer = 0;
          sfx('drop');
        }
      } else if (state === 'close') {
        open = Math.max(0, open - dt * 3);
        if (open <= 0) {
          tryGrab();
          state = 'lift';
          progress = 0;
        }
      } else if (state === 'lift') {
        rope = Math.max(0, rope - 90 * dt);
        progress += dt * 0.25;
        if (rope <= 0) state = 'carry';
      } else if (state === 'carry') {
        x += clamp(HOME - x, -SP * dt, SP * dt);
        progress += dt * 0.25;
        if (Math.abs(x - HOME) < 0.5) {
          state = 'open';
          timer = 0;
        }
      } else if (state === 'open') {
        open = Math.min(1, open + dt * 2.5);
        if (grabbed) {
          grabbed.vy = 0;
          grabbed.rest = false;
          grabbed = null;
        }
        if (timer > 0.9) {
          state = 'reset';
          timer = 0;
        }
      } else if (state === 'reset') {
        open = 1;
        nextPlay();
      }
      if (grabbed) {
        // A wobbly grip may give up on the way.
        if (progress >= slipAt) slip();
        else {
          grabbed.x = x;
          grabbed.y = tipY() + grabbed.r * 0.8;
          grabbed.rot = Math.sin(timer * 5) * 0.12;
        }
      }
      // Anything that lost its support starts falling again.
      for (const p of plush) if (p.rest && !p.inChute && p !== grabbed && restY(p) > p.y + 1) p.rest = false;
      // Falling plushies, lowest first so stacks settle cleanly.
      plush.sort((a, b) => b.y - a.y);
      for (const p of plush) {
        if (p.rest || p === grabbed) continue;
        p.vy += 700 * dt;
        p.y += p.vy * dt;
        if (p.inChute) continue;
        if (p.x < CHUTE_R && p.y > 236) {
          p.inChute = true;
          g.score++;
          flash = 1;
          sfx('fanfare');
          fx.pop(HOME, 230, '+1 🧸', '#ffd84a');
          fx.burst(HOME, 250, ['#ffd84a', '#ff7aa0', '#7fe0c0', '#ffffff'], 24, 120);
          continue;
        }
        const ry = restY(p);
        if (p.y >= ry) {
          p.y = ry;
          if (p.vy > 200) {
            p.vy = -p.vy * 0.25;
            tone(180, 0.06, 'sine', 0.04);
          } else {
            p.vy = 0;
            p.rest = true;
          }
        }
      }
      plush = plush.filter((p) => !(p.inChute && p.y > H + 40));
      fx.update(dt);
      if (endT > 0) {
        endT -= dt;
        if (endT <= 0) g.over = true;
      }
    },
    draw(c, t) {
      nightBg(c, W, H, '#1c1430', '#2a1838');
      // Cabinet body.
      rr(c, 4, 4, W - 8, H - 8, 18, '#ff6fa8');
      rr(c, 10, 10, W - 20, 44, 12, '#ffe0ef');
      // Marquee with chasing lights.
      for (let i = 0; i < 18; i++) {
        const on = Math.floor(t * 6 + i) % 3 === 0 || flash > 0;
        c.fillStyle = on ? '#fff36a' : '#c84a80';
        c.beginPath();
        c.arc(20 + i * 15.3, 14, 2.6, 0, TAU);
        c.arc(20 + i * 15.3, 50, 2.6, 0, TAU);
        c.fill();
      }
      text(c, '🧸 ✦ 🧸 ✦ 🧸', W / 2, 32, 16, '#ff6fa8');
      // Glass interior.
      const ig = c.createLinearGradient(0, TOP, 0, FLOOR);
      ig.addColorStop(0, '#3a2a5a');
      ig.addColorStop(1, '#5a3a7a');
      c.fillStyle = ig;
      c.fillRect(L, TOP, RGT - L, FLOOR - TOP + 8);
      // Back wall polka dots.
      c.fillStyle = 'rgba(255,255,255,0.06)';
      for (let yy = TOP + 12; yy < FLOOR; yy += 24)
        for (let xx = L + 12 + ((yy / 24) % 2) * 12; xx < RGT; xx += 24) {
          c.beginPath();
          c.arc(xx, yy, 4, 0, TAU);
          c.fill();
        }
      // Floor.
      c.fillStyle = '#8a5ab0';
      c.fillRect(L, FLOOR, RGT - L, 8);
      // Chute: a see-through box with a dark hole.
      c.fillStyle = '#1a0e20';
      c.fillRect(L, 240, CHUTE_R - L, FLOOR - 240 + 8);
      rr(c, L, 232, CHUTE_R - L + 4, 10, 4, '#ffd34e');
      c.fillStyle = 'rgba(200,230,255,0.18)';
      c.fillRect(L, 242, CHUTE_R - L, FLOOR - 242);
      text(c, '⬇', HOME, 280 + Math.sin(t * 4) * 3, 18, flash > 0 ? '#fff36a' : '#ffd34e');
      // Plushies.
      const order = plush.filter((p) => p !== grabbed).sort((a, b) => a.y - b.y);
      for (const p of order) {
        if (p.inChute) {
          if (p.y < FLOOR) drawPlush(c, p, t);
        } else drawPlush(c, p, t);
      }
      // Rail + gantry.
      rr(c, L, RAIL - 6, RGT - L, 8, 4, '#c0c0d8');
      rr(c, x - 16, RAIL - 10, 32, 14, 5, '#e0e0f0');
      c.strokeStyle = '#d0d0e0';
      c.lineWidth = 2;
      c.beginPath();
      c.moveTo(x, RAIL + 4);
      c.lineTo(x, tipY() - 18);
      c.stroke();
      if (grabbed) drawPlush(c, grabbed, t);
      // Claw head + three prongs.
      const ty = tipY();
      rr(c, x - 10, ty - 22, 20, 12, 5, '#f4f4ff');
      c.fillStyle = '#ff6fa8';
      c.beginPath();
      c.arc(x, ty - 16, 3, 0, TAU);
      c.fill();
      c.strokeStyle = '#d8d8ec';
      c.lineWidth = 3.5;
      c.lineCap = 'round';
      const spread = 6 + open * 12;
      for (const s of [-1, 1]) {
        c.beginPath();
        c.moveTo(x + s * 6, ty - 12);
        c.lineTo(x + s * spread, ty + 2);
        c.lineTo(x + s * (spread - 6 - open * 2), ty + 14);
        c.stroke();
      }
      c.beginPath();
      c.moveTo(x, ty - 10);
      c.lineTo(x, ty + 10 - open * 4);
      c.stroke();
      c.lineCap = 'butt';
      // Aim shadow on the pile while moving.
      if (state === 'move' && plays > 0) {
        c.setLineDash([3, 5]);
        c.strokeStyle = 'rgba(255,255,255,0.25)';
        c.lineWidth = 1.5;
        c.beginPath();
        c.moveTo(x, ty + 14);
        c.lineTo(x, FLOOR);
        c.stroke();
        c.setLineDash([]);
      }
      // Glass glare.
      c.fillStyle = 'rgba(255,255,255,0.07)';
      c.beginPath();
      c.moveTo(RGT - 70, TOP);
      c.lineTo(RGT - 40, TOP);
      c.lineTo(RGT - 110, FLOOR);
      c.lineTo(RGT - 140, FLOOR);
      c.fill();
      c.strokeStyle = 'rgba(255,255,255,0.35)';
      c.lineWidth = 2;
      c.strokeRect(L, TOP, RGT - L, FLOOR - TOP + 8);
      // Control panel.
      rr(c, 14, 344, W - 28, 64, 12, '#3a2a5a');
      for (let i = 0; i < PLAYS; i++) {
        const on = i < plays;
        c.fillStyle = on ? '#ffd34e' : 'rgba(255,255,255,0.15)';
        c.beginPath();
        c.arc(36 + i * 22, 366, 7, 0, TAU);
        c.fill();
        if (on) text(c, '$', 36 + i * 22, 366, 9, '#8a5a10');
      }
      text(c, `🧸 × ${g.score}`, 36, 392, 15, '#fff3d0', 'left');
      if (state === 'move' && plays > 0 && endT <= 0) {
        const k = clamp(playT / 15, 0, 1);
        rr(c, 160, 360, 110, 10, 5, 'rgba(0,0,0,0.4)');
        rr(c, 160, 360, Math.max(6, 110 * k), 10, 5, k > 0.3 ? '#7fe0c0' : '#ff6a5a');
        text(c, `${Math.ceil(playT)}`, 215, 388, 14, '#fff3d0');
      }
      fx.draw(c);
    },
    stars: (s) => Math.min(5, s),
  };
  return g;
}

defineGame('goldfish', goldfish);
defineGame('ringToss', ringToss);
defineGame('claw', claw);
